import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  apiKeys as apiKeysApi,
  userTokens as userTokensApi,
  type ApiKeyRow,
  type ConnectorTokenRow,
} from "@/lib/api";

const MCP_BASE = "https://coauthor.skarp.app";
// The bare `/mcp` alias is the connect URL we publish; it serves the same
// user-wide server as `/mcp/me/mcp`, which still works for anything
// already configured.
const USER_WIDE_URL = `${MCP_BASE}/mcp`;

/**
 * `/tokens` — manage user-wide connector tokens.
 *
 * The OAuth flow (Claude.ai / ChatGPT / Codex "Add custom connector" → paste
 * the MCP URL → consent) mints a token automatically and labels it with the
 * client name. This page is primarily for *managing* those tokens (list,
 * revoke). Manual minting is still available behind an "Advanced" disclosure
 * for headless agents and CLI tools that can't do an OAuth dance.
 */
export function Tokens() {
  const qc = useQueryClient();
  const [labelDraft, setLabelDraft] = useState("My agent");
  const [justMinted, setJustMinted] = useState<{
    token: string;
    label: string | null;
  } | null>(null);

  const tokensQuery = useQuery({
    queryKey: ["user_wide_tokens"],
    queryFn: () => userTokensApi.list(),
  });

  const mintMut = useMutation({
    mutationFn: (label: string) => userTokensApi.mint(label || undefined),
    onSuccess: (res) => {
      setJustMinted({ token: res.token, label: res.label });
      setLabelDraft("");
      void qc.invalidateQueries({ queryKey: ["user_wide_tokens"] });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (tokenId: string) => userTokensApi.revoke(tokenId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user_wide_tokens"] });
    },
  });

  const tokens = tokensQuery.data?.tokens ?? [];
  const activeCount = tokens.filter((t) => !t.revoked_at).length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
            ← Coauthor
          </Link>
          <h1 className="text-2xl font-semibold mt-1">Your agent tokens</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Connect Claude.ai, Claude Code, ChatGPT, or Codex by adding the
            MCP URL below as a custom connector — the consent flow mints and
            labels a token for you. Tokens you've authorized show up below;
            revoke any you no longer use. See{" "}
            <Link to="/getting-started" className="underline">/getting-started</Link>
            {" "}for per-client setup steps.
          </p>
        </div>
      </header>

      {/* Connection details (always visible) */}
      <section className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4 mb-6">
        <div className="text-sm font-medium mb-2">MCP endpoint URL</div>
        <CopyField value={USER_WIDE_URL} />
        <p className="text-xs text-zinc-500 mt-2">
          Add this URL wherever your client lets you register a custom MCP
          server; the client runs OAuth and stores the bearer for you. The
          exact menu path varies and shifts release to release — see your
          client's own docs, or{" "}
          <Link to="/getting-started" className="underline">/getting-started</Link>
          {" "}for the high-level setup options.
        </p>
      </section>

      {/* Just-minted reveal */}
      {justMinted && (
        <section className="rounded-md border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 p-4 mb-6 space-y-3">
          <div className="flex items-baseline justify-between">
            <div className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
              Token minted{justMinted.label ? ` — ${justMinted.label}` : ""}
            </div>
            <button
              type="button"
              onClick={() => setJustMinted(null)}
              className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
            >
              Dismiss
            </button>
          </div>
          <div className="text-xs text-emerald-800 dark:text-emerald-200">
            Save this now. It cannot be retrieved later.
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">
              Bearer token
            </div>
            <CopyField value={justMinted.token} mono />
          </div>
        </section>
      )}

      {/* Token list */}
      <section>
        <div className="text-sm font-medium mb-2">
          Active tokens ({activeCount})
        </div>
        {tokensQuery.isLoading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="text-sm text-zinc-500 italic">
            No tokens yet. Add the MCP URL above as a custom connector in
            Claude.ai, Claude Code, ChatGPT, or Codex to get one.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-md border border-zinc-200 dark:border-zinc-800">
            {tokens.map((t) => (
              <TokenRow
                key={t.id}
                token={t}
                onRevoke={() => revokeMut.mutate(t.id)}
                revoking={revokeMut.isPending && revokeMut.variables === t.id}
              />
            ))}
          </ul>
        )}
      </section>

      <ApiKeysSection />

      {/* Advanced: manual minting for CLI / headless agents */}
      <details className="mt-6 rounded-md border border-zinc-200 dark:border-zinc-800">
        <summary className="px-4 py-3 text-sm font-medium cursor-pointer select-none hover:bg-zinc-50 dark:hover:bg-zinc-900">
          Advanced: generate a token manually
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-zinc-200 dark:border-zinc-800 space-y-3">
          <p className="text-xs text-zinc-500">
            Most users don't need this — Claude.ai, Claude Code, ChatGPT, and
            Codex all do OAuth automatically. Use manual minting only for
            scripts or custom clients that can't run an OAuth flow.
          </p>
          <div className="flex gap-2 items-start">
            <input
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              placeholder="Optional label (e.g. CLI script)"
              maxLength={80}
              className="flex-1 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => mintMut.mutate(labelDraft.trim())}
              disabled={mintMut.isPending}
              className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 disabled:opacity-50"
            >
              {mintMut.isPending ? "Minting…" : "Generate"}
            </button>
          </div>
          {mintMut.error && (
            <div className="text-xs text-red-600 dark:text-red-400">
              {(mintMut.error as ApiError).body
                ? ((mintMut.error as ApiError).body as { error?: string }).error
                : String(mintMut.error)}
            </div>
          )}
          <p className="text-xs text-zinc-500">
            The token is shown once. Save it now — it cannot be retrieved
            later.
          </p>
        </div>
      </details>
    </div>
  );
}

/**
 * API keys for the `coauthor` CLI and the public /v1 API.
 *
 * Separate from connector tokens above because they're obtained differently:
 * connector tokens are minted for you by a client's OAuth flow, whereas a CLI
 * has no browser to run one — you paste a key in. Both resolve to the same
 * user (see server/identity.py), so this is a packaging distinction, not two
 * identities.
 */
function ApiKeysSection() {
  const qc = useQueryClient();
  const [labelDraft, setLabelDraft] = useState("My laptop");
  const [justMinted, setJustMinted] = useState<string | null>(null);

  const keysQuery = useQuery({
    queryKey: ["api_keys"],
    queryFn: () => apiKeysApi.list(),
  });

  const mintMut = useMutation({
    mutationFn: (label: string) => apiKeysApi.mint(label || undefined),
    onSuccess: (res) => {
      setJustMinted(res.api_key);
      setLabelDraft("");
      void qc.invalidateQueries({ queryKey: ["api_keys"] });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (keyId: string) => apiKeysApi.revoke(keyId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api_keys"] }),
  });

  const keys = keysQuery.data?.keys ?? [];
  const active = keys.filter((k) => !k.revoked_at);

  return (
    <section className="mt-8">
      <div className="text-sm font-medium mb-1">Command-line keys</div>
      <p className="text-xs text-zinc-500 mb-3">
        For the <code className="font-mono">coauthor</code> CLI and the public
        API. Install the CLI, then run{" "}
        <code className="font-mono">coauthor auth &lt;key&gt;</code> once — it's
        stored in your home directory, so you don't need to re-paste it in every
        shell.
      </p>

      {justMinted && (
        <div className="rounded-md border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 p-4 mb-3 space-y-3">
          <div className="flex items-baseline justify-between">
            <div className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
              Key created
            </div>
            <button
              type="button"
              onClick={() => setJustMinted(null)}
              className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
            >
              Dismiss
            </button>
          </div>
          <div className="text-xs text-emerald-800 dark:text-emerald-200">
            Save this now. It cannot be retrieved later.
          </div>
          <CopyField value={justMinted} mono />
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">
              Then run
            </div>
            <CopyField value={`coauthor auth ${justMinted}`} mono />
          </div>
        </div>
      )}

      {keysQuery.isLoading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : active.length === 0 ? (
        <div className="text-sm text-zinc-500 italic mb-3">
          No keys yet.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-md border border-zinc-200 dark:border-zinc-800 mb-3">
          {keys.map((k) => (
            <ApiKeyRowItem
              key={k.id}
              apiKey={k}
              onRevoke={() => revokeMut.mutate(k.id)}
              revoking={revokeMut.isPending && revokeMut.variables === k.id}
            />
          ))}
        </ul>
      )}

      <div className="flex gap-2 items-start">
        <input
          type="text"
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          placeholder="Optional label (e.g. My laptop)"
          maxLength={80}
          className="flex-1 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm"
        />
        <button
          type="button"
          onClick={() => mintMut.mutate(labelDraft.trim())}
          disabled={mintMut.isPending}
          className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 disabled:opacity-50"
        >
          {mintMut.isPending ? "Creating…" : "Create key"}
        </button>
      </div>
      {mintMut.error && (
        <div className="text-xs text-red-600 dark:text-red-400 mt-2">
          {(mintMut.error as ApiError).body
            ? ((mintMut.error as ApiError).body as { error?: string }).error
            : String(mintMut.error)}
        </div>
      )}
    </section>
  );
}

function ApiKeyRowItem({
  apiKey,
  onRevoke,
  revoking,
}: {
  apiKey: ApiKeyRow;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const isRevoked = !!apiKey.revoked_at;
  return (
    <li className={`px-4 py-3 flex items-center justify-between ${isRevoked ? "opacity-50" : ""}`}>
      <div className="min-w-0 mr-4">
        <div className="text-sm font-medium truncate">
          {apiKey.label || <span className="text-zinc-500 italic">Unnamed</span>}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono">cik_{apiKey.prefix}…</span>
          <span>·</span>
          <span>
            created{" "}
            {apiKey.created_at ? new Date(apiKey.created_at).toLocaleDateString() : "?"}
          </span>
          {apiKey.last_used_at && (
            <>
              <span>·</span>
              <span>last used {new Date(apiKey.last_used_at).toLocaleString()}</span>
            </>
          )}
          {apiKey.call_count > 0 && (
            <>
              <span>·</span>
              <span>{apiKey.call_count} calls</span>
            </>
          )}
          {isRevoked && (
            <>
              <span>·</span>
              <span className="text-red-600 dark:text-red-400">revoked</span>
            </>
          )}
        </div>
      </div>
      {!isRevoked && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 flex-shrink-0"
        >
          {revoking ? "Revoking…" : "Revoke"}
        </button>
      )}
    </li>
  );
}

function CopyField({ value, mono = false }: { value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };
  return (
    <div className="flex gap-2 items-stretch">
      <input
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className={`flex-1 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-xs ${mono ? "font-mono" : ""}`}
      />
      <button
        type="button"
        onClick={handleCopy}
        className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-900 flex-shrink-0"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function TokenRow({
  token,
  onRevoke,
  revoking,
}: {
  token: ConnectorTokenRow;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const isRevoked = !!token.revoked_at;
  return (
    <li className={`px-4 py-3 flex items-center justify-between ${isRevoked ? "opacity-50" : ""}`}>
      <div className="min-w-0 mr-4">
        <div className="text-sm font-medium truncate">
          {token.label || <span className="text-zinc-500 italic">Unnamed</span>}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono">coauth_{token.prefix}…</span>
          <span>·</span>
          <span>created {token.created_at ? new Date(token.created_at).toLocaleDateString() : "?"}</span>
          {token.last_used_at && (
            <>
              <span>·</span>
              <span>last used {new Date(token.last_used_at).toLocaleString()}</span>
            </>
          )}
          {isRevoked && (
            <>
              <span>·</span>
              <span className="text-red-600 dark:text-red-400">revoked</span>
            </>
          )}
        </div>
      </div>
      {!isRevoked && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 flex-shrink-0"
        >
          {revoking ? "Revoking…" : "Revoke"}
        </button>
      )}
    </li>
  );
}
